import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminLogin, getCurrentAdmin } from '../services/adminApi';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Vérifier si l'admin est connecté au chargement
  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('admin_access_token');
      if (token) {
        try {
          console.log('🔍 Vérification du token admin:', token.substring(0, 20) + '...');
          const adminData = await getCurrentAdmin();
          console.log('✅ Admin connecté:', adminData);
          setAdmin(adminData);
        } catch (error) {
          console.error('❌ Erreur lors de la vérification de l\'authentification admin:', error);
          console.error('   Détails:', error.message);
          localStorage.removeItem('admin_access_token');
          setError('Session expirée. Veuillez vous reconnecter.');
        }
      } else {
        console.log('ℹ️ Aucun token admin trouvé');
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Fonction de connexion admin
  const login = async (credentials) => {
    try {
      setError(null);
      const response = await adminLogin(credentials);
      
      // Stocker le token
      localStorage.setItem('admin_access_token', response.access_token);
      
      // Récupérer les informations de l'admin
      const adminData = await getCurrentAdmin();
      setAdmin(adminData);
      
      return { success: true, admin: adminData };
    } catch (error) {
      setError(error.message || 'Erreur de connexion');
      return { success: false, error: error.message };
    }
  };

  // Fonction de déconnexion admin
  const logout = () => {
    localStorage.removeItem('admin_access_token');
    setAdmin(null);
    setError(null);
  };

  // Fonction pour mettre à jour les informations de l'admin
  const updateAdmin = (adminData) => {
    setAdmin(adminData);
  };

  // Fonction pour effacer les erreurs
  const clearError = () => {
    setError(null);
  };

  // ==================== FONCTIONS DE GESTION DES PERMISSIONS ====================

  // Vérifier si l'admin est un super admin
  const isSuperAdmin = () => {
    return admin?.is_superadmin || false;
  };

  // Vérifier si l'utilisateur est un admin
  const isAdmin = () => {
    return !!admin;
  };

  // Vérifier si l'utilisateur a une permission spécifique
  const hasPermission = (permission) => {
    if (!admin) return false;
    
    // Super admin a toutes les permissions
    if (admin.is_superadmin) {
      return true;
    }
    
    // Permissions spécifiques pour les admins normaux
    const adminPermissions = [
      'view_dashboard',
      'view_users', 
      'view_restaurants', 
      'view_dishes', 
      'view_orders', 
      'view_complaints', 
      'view_payments',
      'manage_users',
      'manage_restaurants',
      'manage_dishes',
      'manage_orders',
      'manage_complaints',
      'manage_payments'
    ];
    
    return adminPermissions.includes(permission);
  };

  // Exiger les droits de super admin
  const requireSuperAdmin = () => {
    if (!isSuperAdmin()) {
      throw new Error('Super Admin access required');
    }
  };

  // Exiger les droits d'admin
  const requireAdmin = () => {
    if (!isAdmin()) {
      throw new Error('Admin access required');
    }
  };

  // Obtenir les headers d'authentification
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const value = {
    admin,
    loading,
    error,
    login,
    logout,
    updateAdmin,
    clearError,
    isAuthenticated: !!admin,
    // Fonctions de permissions
    isSuperAdmin,
    isAdmin,
    hasPermission,
    requireSuperAdmin,
    requireAdmin,
    getAuthHeaders,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}; 